<template>
    <section class='create-project content box'>
        <b-tabs v-model="activeTab" @change="tabChanged">

            <b-tab-item label="Overview">
                <center>
                    <h2 class='title is-3'>Crowdfunding Overview</h2>
                    <h4 class='subtitle is-6'>Raise money to fund your project!</h4>
                    <br/>
                </center>

                <p>
                    If you're looking to crowdfund your project, Biaggeo can help!
                    Crowdfunding allows anyone, including students, alumni, friends and family,
                    and strangers to donate to your project. You specify how much you want
                    to raise, then you have 60 days to reach that amount. If successful,
                    the funds will be disbursed to a bank account of your choice. If the
                    target is not hit within 60 days, all funds are refunded to donors.
                </p>

                <p>
                    Biaggeo requires you to fill out an application, which must be approved
                    by a site administrator, in order to start your crowdfunding campaign.
                    This allows us to:
                    <ul>
                        <li>maintain a standard of quality,</li>
                        <li>ensure campaigns are legitimate,</li>
                        <li>and inspire confidence in interested donors</li>
                    </ul>
                </p>
                <p>
                    The application covers topics like risk involved in your project,
                    factors that will help you succeed, and details about your team. Once
                    you begin an application, you may revise it at any time until you submit
                    it for approval. If an administrator requests changes, you'll be emailed
                    and your application may be revised again.
                </p>


                <!-- <div id='feature-icons' class='columns has-text-centered'>
                    <div class='column'>
                        <b-icon icon="account-multiple" custom-class='icon-bg-blue'></b-icon><br/>
                        <span class='feature-title'>Matchmaking</span>
                        <p>Find teammates from your university to make your idea a reality</p>
                    </div>
                    <div class='column'>
                        <b-icon icon="currency-usd" custom-class='icon-bg-green'></b-icon><br/>
                        <span class='feature-title'>Crowdfunding</span>
                        <p>Raise capital from students, alumni, family, and even strangers</p>
                    </div>
                    <div class='column'>
                        <b-icon icon="bullhorn" custom-class='icon-bg-red'></b-icon><br/>
                        <span class='feature-title'>Networking</span>
                        <p>Share your progress with alumni and connect with users and customers</p>
                    </div>
                </div> -->
                <h3 class='title is-6'>Key details about crowdfunding on our platform:</h3>

                <b-icon icon="clock"></b-icon>
                <span>Crowdfunding campaigns last: <strong>60 days</strong></span>

                <br/>

                <b-icon icon="cash-multiple"></b-icon>
                <span>Overfunding allowed: <strong>yes</strong></span>

                <br/><br/>

                <h3 class='title is-6'>How is my application reviewed?</h3>

                <div id='review-criteria'>
                    <b-icon icon="check"></b-icon> <span>Reasonable</span>
                    <p>Is the amount you're asking for both reasonable
                        and achievable within a 60 day period?</p>

                    <b-icon icon="account-multiple"></b-icon> <span>Team</span>
                    <p>Do you have the right people on your team to achieve
                        the project's stated goals?</p>

                    <b-icon icon="star"></b-icon> <span>Followers</span>
                    <p>Has your project garnered some attention? Are people
                        following your project? Is there a sense of media presence?</p>

                    <b-icon icon="calendar-today"></b-icon> <span>Days on Biaggeo</span>
                    <p>Has the project been running for a little bit?
                        A project that was just created yesterday may not yet have the exposure
                        needed to reach a crowdfunding goal, compared to a project that has
                        been on the platform for a month.</p>

                    <b-icon icon="seal"></b-icon> <span>Overall quality</span>
                    <p>Do your project page and application adequately
                        explain your story, goals, and other details that people may want
                        to learn? Does the project page excite and inspire?</p>
                </div>

                <br/><br/>

                <div class='has-text-centered'>
                    <div v-if="!application">
                        <h4 class='title is-5'>You do not have an existing application</h4>
                        <b-button class='is-large is-primary' :loading="isCreating" @click="createApplication">
                            Start an application
                        </b-button>
                    </div>
                    <div v-if="application">
                        <h4 class='title is-5'>You have an existing application</h4>
                        <b-button class='is-large is-primary' @click="activeTab = 1">
                            View application
                        </b-button>
                    </div>
                </div>
            </b-tab-item>

            <b-tab-item label="Application">
                <div class='well' v-if="!application">
                    <h3 class='title is-4'>No crowdfund application</h3>
                    <h4 class='subtitle is-6'>This project has no existing application to begin crowdfunding</h4>
                    <p>
                        You have no existing crowdfund application for this project. If you'd like to
                        start a crowdfunding campaign, go back to the previous tab and click the button
                        to start.
                    </p>
                </div>
                <div v-if="application">
                    <b-message type="is-info" has-icon v-if="application.readyForReview">
                        <strong>This application is under review by a site administrator.</strong> If approved, your crowdfunding campaign
                        will begin. If rejected, you'll receive an email (the one listed on your profile) with an explanation
                        of why it was turned down. You may then make as many edits to address any concerns and re-submit
                    </b-message>

                    <label class="label">Amount ($100 - $100,000)</label>
                    <b-field :type="amountValid ? 'is-success' : 'is-danger'">
                        <p class="control">
                            <span class="button is-static">
                                <b-icon icon="currency-usd Amount"></b-icon>
                            </span>
                        </p>
                        <b-input type="number" min="100" max="100000"
                            validation-message=" "
                            v-model="application.targetAmount"
                            :disabled="application.readyForReview"></b-input>
                    </b-field>

                    <b-field v-for="question in application.questions" :key="question.q" :label="question.q">
                        <b-input maxlength="2000" type="textarea" v-model="question.a" :disabled="application.readyForReview">{{question.a}}</b-input>
                    </b-field>
                </div>

                <div class='has-text-centered' v-if="application && !application.readyForReview">
                    <hr/>

                    <b-message type="is-warning" v-if="appReadyForReview" has-icon>
                        <p>When <em>Ready for review</em> is checked, you may click the {{saveBtnText}} button to submit this
                        crowdfunding application. A site administrator will review it. It it's approved, your crowdfunding
                        campaign will begin. If rejected, you may make edits and re-submit it.</p>
                        <p><strong>Be aware</strong> <em>While an application is under review, edits cannot be made!</em></p>
                    </b-message>
                    <b-checkbox v-model="appReadyForReview" type="is-warning" style="margin-top:7px;margin-right:10px;">Ready for review</b-checkbox>
                    <b-button :icon-left="saveBtnIcon" @click="saveApplication" :loading="isSaving">{{saveBtnText}}</b-button>
                </div>
            </b-tab-item>

        </b-tabs>
    </section>
</template>

<style>
    .create-project { font-size: 90%; }
    .create-project .col {
        padding: 0 10px;
    }
    .create-project.box {
        width: 50% !important;
        margin: auto;
    }
    .feature-title {
        font-size: 1.3em;
        letter-spacing: 0px;
    }
    #feature-icons span.icon {
        margin: 10px 0 15px 0;
    }
    #feature-icons i {
        padding: 6px 12px;
        border-size: 1px;
        border-style: solid;
        border-radius: 100%;
    }
    #review-criteria { margin-left: 0px; }
    #review-criteria span { margin-left: 1px; font-style: italic; }
    #review-criteria p { margin-left: 30px; }
</style>

<script>
export default {
  name: 'ProjectCrowdfund',
  data () {
    return {
        activeTab: 0,
        projectId: 0,
        isCreating: false,
        isSaving: false,

        appReadyForReview: false,

        /* https://stackoverflow.com/a/34828165 */
        application: false
    }
  },
  computed: {
    saveBtnText() {
      return this.appReadyForReview ? 'Submit' : 'Save draft';
    },
    saveBtnIcon() {
      return this.appReadyForReview ? 'arrow-right' : 'content-save';
    },
    amountValid() {
      return this.application && this.application.targetAmount >= 100 && this.application.targetAmount <= 100000;
    }
  },
  mounted() {
      this.projectId = this.$route.params.projectId;

      this.$http.get(this.$hostname + '/crowdfundApplication/' + this.projectId).then(function(response) {
        // The API will respond with text "OK" if a project is valid but has no crowdfund application
        if(response.body !== 'OK') this.application = response.body;
      });
  },
  methods: {
    tabChanged(index) {
        // Scroll to top of page, most importantly for filling out an application
        this.$root.scrollToTop();
    },
    createApplication: function() {
        this.isCreating = true;
        this.$http.put(this.$hostname + '/crowdfundApplication/' + this.projectId).then(function(response) {
            this.application = response.body;
            this.activeTab = 1;
        }).catch(function(error) {
            this.$toast.open({
              message: 'Unable to create application - please try again',
              type: 'is-danger',
              queue: false
            });
        }).finally(function() {
            this.isCreating = false;
        });
    },
    saveApplication: function() {
        this.isSaving = true;

        if(this.appReadyForReview) this.application.readyForReview = true;

        this.$http.post(this.$hostname + '/crowdfundApplication/' + this.projectId, this.application).then(function(response) {
            this.$toast.open({
              message: (this.application.readyForReview) ? 'Submitted application for review!' : 'Saved edits to application',
              type: 'is-success',
              queue: false
            });
            this.$root.scrollToTop();
        }).catch(function(error) {
            // If an error occurred and the user specified it's ready to review,
            // undo this UI change so the form does not become disabled
            if(this.appReadyForReview) this.application.readyForReview = false;

            this.$toast.open({
              message: 'Unable to save application - please try again',
              type: 'is-danger',
              queue: false
            });
        }).finally(function() {
            this.isSaving = false;
        });
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/ProjectCrowdfund.vue